// askSmart-ui/src/pages/TOSP/tenantContacts.ts
import {
  assignedIndices,
  isTenantService,
} from "./components/LeaseReview/leaseSource";

// Who can be made a tenant.
//
// A tenant in TOSP is a business, but the paperwork belongs to a person — the
// contact who signs the lease and logs into the portal. That person already
// exists in SMRT Docs, where an admin has put them on the "Tenant" template,
// so the list of candidates is a fact the org has already stated rather than
// something to be typed again here.
//
// Starting from that list is also what makes the lease reachable. The lease is
// found through the contact's SMRT Docs record (see leaseSource), so a tenant
// created from a contact who isn't on the template has no lease TOSP can open
// — which is exactly the dead end this list avoids.
//
//   /smrtdocs/services     → which service index is "Tenant"
//   /smrtdocs/assignments  → userId → the service indices assigned to them
//   /approved_users        → the name, email and phone to prefill with

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export interface TenantContact {
  userId: string;
  name: string;
  email: string;
  phone: string;
}

export type TenantContactLookup =
  | { status: "ok"; contacts: TenantContact[] }
  | { status: "error"; reason: string };

const getJson = async (path: string): Promise<any> => {
  const res = await fetch(`${BACKEND}${path}`, { credentials: "include" });
  if (!res.ok) throw new Error(`${path} returned ${res.status}`);
  return res.json();
};

/** Everyone the org has put on a tenant service, in name order. */
export async function fetchTenantContacts(): Promise<TenantContactLookup> {
  try {
    const [serviceData, assignmentData, userData] = await Promise.all([
      getJson("/smrtdocs/services"),
      getJson("/smrtdocs/assignments"),
      getJson("/approved_users"),
    ]);

    const services: any[] = serviceData.services || [];
    // Every tenant service, not one named "Tenant" — each tenant has their own
    // copy now, so the candidates are everyone on any of them.
    const tenantIndices = new Set(
      services
        .map((service, index) => ({ service, index }))
        .filter(({ service }) => isTenantService(service?.name))
        .map(({ index }) => index),
    );
    if (tenantIndices.size === 0) {
      return {
        status: "error",
        reason: `No SMRT Docs tenant service exists. Found: ${
          services.map((service) => `“${service?.name}”`).join(", ") || "none"
        }.`,
      };
    }

    const assignments: Record<string, any> = assignmentData.assignments || {};
    const users: any[] = userData.users || userData || [];

    const contacts = users
      .filter((user) =>
        assignedIndices(assignments[user.user_id || user.id]).some((index) =>
          tenantIndices.has(index),
        ),
      )
      .map((user) => ({
        userId: user.user_id || user.id,
        name: (user.name || "").trim(),
        email: (user.email || "").trim(),
        phone: (user.phone || "").trim(),
      }))
      // Without an email the lease can't be found and the portal can't be
      // reached, so the contact isn't usable as a tenant.
      .filter((contact) => contact.email)
      .sort((a, b) =>
        (a.name || a.email).localeCompare(b.name || b.email, undefined, {
          sensitivity: "base",
        }),
      );

    return { status: "ok", contacts };
  } catch (err) {
    console.error("[TOSP] tenant contact lookup failed:", err);
    return {
      status: "error",
      reason: `Couldn't reach SMRT Docs: ${
        err instanceof Error ? err.message : "unknown error"
      }`,
    };
  }
}
